import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { browserHistory } from 'utils/navigation'
import { Button, Container, SortableTable, Loader, TextOverflow } from 'components/UIElements'
import { convertToLocalizedNumber, parseSpecialCharactersIn } from 'utils/misc'
import { buttonContentOnSendState } from 'utils/helpers'
import { getFormattedDateString, getDateFromNow, yearDifference } from 'utils/time'
import {
  FLAGSMITH_FEATURES,
  MODAL_BUTTONS_MAP,
  MODAL_CLASSES_MAP,
  MODAL_CONTENT_MAP,
  EMPTY_VALUE,
  PARTICIPANT_ID,
  PRE_SCREEN_ID,
  SITE_NAME,
  SUBJECT_ID,
  PARTICIPANT_KEYS,
  DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION,
} from 'utils/constants'
import useFlagsmithFeature from 'utils/hooks/useFlagsmithFeature'
import useInstrumentUnificationFeature from 'utils/hooks/useInstrumentUnificationFeature'
import STRINGS from 'utils/strings'
import 'react-datepicker/dist/react-datepicker.css'
import ParticipantVisits from './ParticipantVisits'
import NewConsentManagement from './NewConsentManagement'
import FilesManagement from './FilesManagement'
import ParticipantForm from './ParticipantForm'
import CohortManagement from '../containers/CohortManagementContainer'
import {
  getTotals,
  headerOptions,
  renderFunctions,
  treasureChestHeaderOptions,
} from '../../../../Payments/components/PaymentsTable'
import CreateParticipantNav from './CreateParticipantNav'
import CompletedInstruments from './CompletedInstruments'
import CompletedAnnouncements from './CompletedAnnouncements'
import CompletedDiaries from './CompletedDiaries'
import ParticipantStats from './ParticipantStats'
import ParticipantProfile from './ParticipantProfile'
import ParticipantStudySettings from './ParticipantStudySettings'
import VisitScheduleManagement from './VisitScheduleManagement'
import '../stylesheets/participant.scss'
import AccountSettings from './AccountSettings'
import { onOpenCancelModal } from '../../../utils/pariticipantUtils'
import DrawerTabs from './DrawerTabs'
import StudyTimeline from './StudyTimeline'
import ParticipantVisitCycle from './ParticipantVisitCycle'
import { validateParticipant } from '../modules/CreateParticipant'
import { shouldHideParticipantInstrumentStatus } from 'utils/studyConfigUtils'
import ArmManagement from './ArmManagement'
import ParticipantArms from './ParticipantArms'
import ParticipantHistory from './ParticipantHistory'

const CreateParticipantPage = props => {
  const {
    isCreate,
    ptpId,
    canSeeOrphans,
    participant,
    inLeafSite,
    onSetOrphanedParticipants,
    userId,
    roleId,
    studyID,
    study,
    isArmsSupported = false,
    isArmsDisplayed = false,
    isEnroller = false,
    isClinOrSiteCoordinator = false,
    subjectIdSiteBased = false,
    tracksEnabled,
  } = props
  const pageRef = useRef()

  const flagsmithFeatures = useFlagsmithFeature({
    /**
     * For the featureFlags key, we put in a object for what the default value of
     * the feature flag(s) should be in case there is an error with the Flagsmith API.
     */
    featureFlags: { [FLAGSMITH_FEATURES.advancedEvents]: { enabled: true } },
    userId,
    studyId: studyID,
  })

  const { hasInstrumentUnification } = useInstrumentUnificationFeature({ study, userId })

  const hasAdvancedEventTimelines = flagsmithFeatures[FLAGSMITH_FEATURES.advancedEvents]?.enabled
  const isV2Architecture =
    (study?.config?.instrument_architecture_version || DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION) >
    DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION

  const [editable, setEditable] = useState(isCreate || !!ptpId)
  const [language, setLanguage] = useState('en-US')

  const [checkedVisitTemplate, setCheckedVisitTemplate] = useState({})
  const [checkedVisitCycle, setCheckedVisitCycle] = useState({})
  const [checkedArm, setCheckedArm] = useState()
  const [visitTemplateName, setVisitTemplateName] = useState('')
  const [armError, setArmError] = useState('')
  const [participantVisitsFetched, setParticipantVisitsFetched] = useState(false)

  useEffect(() => {
    const {
      fetchPtp,
      fetchPtpPayments,
      hasTreasureChest,
      inDrawer,
      inLeafSite,
      isInParticipantsTable,
      onSetOrphanedParticipants,
      params,
      ptpSiteID,
      setNavBarProps,
      study,
      toggleOnSite,
      leafSites,
    } = props

    // Only set orphaned participants if there are leaf sites in the current study version
    const hasLeafSites = leafSites && leafSites.length > 0
    if (canSeeOrphans && !isCreate && !inLeafSite && !inDrawer && hasLeafSites) {
      onSetOrphanedParticipants(true)
    }

    setLanguage(navigator.language)

    const { studyID, studyVersionID } = params

    if (((ptpId && inDrawer) || !isInParticipantsTable) && !isCreate) {
      fetchPtp(studyID, ptpSiteID, ptpId)
      fetchPtpPayments(studyID, ptpId, hasTreasureChest)
    }

    setNavBarProps({
      back: `/studies/${study.id}/${studyVersionID}/participants`,
      close: `/studies/${study.id}/${studyVersionID}/participants`,
      backText: 'Participants',
    })

    if (ptpId) {
      toggleOnSite(ptpSiteID)
    }
    pageRef.current.scrollIntoView(true)
    return () => {
      const {
        resetCheckedSites,
        params: { participantId },
      } = props
      setNavBarProps(null)
      if (participantId) resetCheckedSites()
    }
  }, [])

  useEffect(() => {
    const { leafSites } = props
    const ptpLoaded = Object.keys(participant).length !== 0
    const hasLeafSites = leafSites && leafSites.length > 0
    // Only set orphaned participants if there are leaf sites in the current study version
    if (canSeeOrphans && ptpLoaded && !!ptpId && !inLeafSite && hasLeafSites) {
      onSetOrphanedParticipants(true)
    }
  }, [canSeeOrphans, participant, inLeafSite, onSetOrphanedParticipants, ptpId, props.leafSites])

  const triggerEditable = () => {
    setEditable(true)
  }

  const getCompletion = () => {
    const { participant } = props
    const { num_completed_instruments, num_total_instruments } = participant
    const completed = num_completed_instruments || 0
    const total = num_total_instruments || 100
    return `${Math.floor((completed / total) * 100)}% Compliance`
  }

  const onSaveParticipant = () => {
    const {
      isEmailless,
      baseSiteID,
      ptpSiteID,
      saveToDatabase,
      study,
      participant,
      checkedCohorts,
      consentRequired,
      checkedSites,
      participantDataFields,
      params: { participantId, studyVersionID },
      studyID,
    } = props

    saveToDatabase({
      studyID: study.id,
      siteID: ptpSiteID != null ? ptpSiteID : baseSiteID,
      participant,
      checkedCohorts,
      participantID: participant.id,
      consentRequired,
      sites: checkedSites,
      pDataFields: participantDataFields,
      redirect:
        !participantId && isEmailless ? `/studies/${studyID}/${studyVersionID}/participants/account-settings` : null,
      armId: isArmsSupported ? checkedArm?.key : undefined,
      useFormSitesForPatch: true, // use site(s) selected in form so profile site change is sent
    })
  }

  const onValidateParticipant = () => {
    const { participant, checkedCohorts, participantDataFields } = props

    const selectedTracks = Object.keys(checkedCohorts)

    return validateParticipant({
      participant,
      pDataFields: participantDataFields,
      selectedTracks: selectedTracks.length ? selectedTracks : null,
      isPatch: !isCreate,
      isVisitCycleSupported,
    })
  }

  const renderPaymentsSection = name => {
    const { componentLoading, hasTreasureChest, payments } = props
    const { pendingPayments: paymentsLoading } = componentLoading
    const hasPayments = payments && payments.completedList.length > 0

    const _headerOptions = hasTreasureChest ? treasureChestHeaderOptions.slice(3) : headerOptions.slice(3)
    return (
      <Container>
        <div className='title'>Payments</div>
        {paymentsLoading && <Loader inContainer />}
        {!paymentsLoading && hasPayments && (
          <div className='payments-table'>
            <SortableTable
              sortingBy='name'
              rowList={payments.completedList}
              headerOptions={_headerOptions}
              renderFunctions={renderFunctions}
            />
            {getTotals(payments.completedList, payments.totals, language, hasTreasureChest)}
          </div>
        )}
        {!paymentsLoading && !hasPayments && <p>{`${name} does not have any payment activity.`}</p>}
      </Container>
    )
  }

  const renderAccountDeletion = (deactivationRequestTimestamp, canSeeAccountSettings) => {
    const deletionMessage = `Account deletion request received ${getFormattedDateString(
      deactivationRequestTimestamp,
      'DD/MM/YYYY',
    )} ( ${getDateFromNow(deactivationRequestTimestamp)} ). Please review the participant's account settings.`
    return (
      <>
        {deactivationRequestTimestamp && (
          <div className='account-deletion'>
            <h6>Account Deletion Request</h6>
            <p>{deletionMessage}</p>
          </div>
        )}
        {canSeeAccountSettings && <AccountSettings {...props} />}
      </>
    )
  }

  const getSiteLocation = (ptpSiteID, sitesInfo) => {
    let text = ''
    let color = '#DDDFF0'
    const siteLocation = ptpSiteID ? sitesInfo[ptpSiteID] : null
    if (!siteLocation) {
      text = 'Site Unassigned'
      color = '#FFC191'
    } else if (siteLocation.is_virtual) {
      text = 'Site Location: Virtual'
      color = '#DDDFF0'
    } else {
      color = '#DDDFF0'
      const siteLocationNameArr = siteLocation.site_path.split('.')
      const regionName = parseSpecialCharactersIn(siteLocationNameArr[2])
      text = `Site Location: ${regionName ? `${regionName}, ` : ''} ${parseSpecialCharactersIn(
        siteLocationNameArr[1],
      ) || ''}`
    }
    return {
      text,
      color,
      label: siteLocation?.site_label || null,
    }
  }

  const {
    canAddPtp,
    canMove,
    canResendInviteOrResetPw,
    canUpdateConsent,
    canViewAnnouncements,
    canViewFiles,
    hasVisitSupport,
    canViewVisitSchedule,
    canWrite,
    closeDrawer,
    consentRequired,
    clearPtpVisits,
    clearPtpVisitCycles,
    fetchParticipantVisits,
    fetchVisitTemplates,
    hasIncentives,
    inDrawer,
    isDiary,
    link,
    locales,
    noPII,
    participantDataFields,
    participantVisits,
    requestSending,
    requestSent,
    sendDisabled,
    studyLock,
    updateField,
    visitTemplates,
    ptpSiteID,
    sitesInfo = {},
    ptpInstrumentsLoading,
    instrumentStatus,
    isSubjectIdAvailable,
    visitStartDatetime,
    isVisitCycleSupported = false,
    participantVisitCycles,
    studyVisitSchedule,
    openModal,
    closeModal,
    isReadOnlyState,
    hasStudyPromotion,
    canViewHistory,
    hideDinamically,
    isCaregiverV1 = false,
  } = props

  useEffect(() => {
    if (!inDrawer || !hasVisitSupport || !studyID || studyVisitSchedule?.length) {
      return
    }
    fetchVisitTemplates(studyID)
  }, [inDrawer, hasVisitSupport, studyID, studyVisitSchedule?.length, fetchVisitTemplates])

  useEffect(() => {
    if (!inDrawer || isCreate || !ptpId || !hasVisitSupport || !canViewVisitSchedule) {
      setParticipantVisitsFetched(false)
      return undefined
    }

    let isCancelled = false
    setParticipantVisitsFetched(false)
    clearPtpVisits()
    clearPtpVisitCycles()

    Promise.resolve(fetchParticipantVisits(studyID, ptpId)).finally(() => {
      if (!isCancelled) {
        setParticipantVisitsFetched(true)
      }
    })

    return () => {
      isCancelled = true
      clearPtpVisits()
      clearPtpVisitCycles()
      setParticipantVisitsFetched(false)
    }
  }, [
    inDrawer,
    isCreate,
    ptpId,
    hasVisitSupport,
    canViewVisitSchedule,
    studyID,
    fetchParticipantVisits,
    clearPtpVisits,
    clearPtpVisitCycles,
  ])

  const ptpSiteLocation = getSiteLocation(ptpSiteID, sitesInfo)
  const { fname, lname, id, deactivation_request_timestamp, email, phone, date_of_birth, subject_id } = participant
  const name = noPII ? `Participant ${id}` : `${fname} ${lname}`
  // Only show need to reassign if there are leaf sites in the current study version
  const hasLeafSites = props.leafSites && props.leafSites.length > 0
  const needToReassign = !isCreate && canSeeOrphans && !participant?.in_leaf_site && hasLeafSites
  const canSave = canWrite || (isCreate && canAddPtp)
  const preScreenId = participant.pre_screen_id ?? null
  const isPreScreenAllowed =
    participantDataFields?.mandatory?.some(f => f.metadata?.internal_key === PARTICIPANT_KEYS.PRE_SCREEN_ID) ||
    participantDataFields?.optional?.some(f => f.metadata?.internal_key === PARTICIPANT_KEYS.PRE_SCREEN_ID)
  const isSubjectAllowed =
    participantDataFields?.mandatory?.some(f => f.metadata?.internal_key === PARTICIPANT_KEYS.SUBJECT_ID) ||
    participantDataFields?.optional?.some(f => f.metadata?.internal_key === PARTICIPANT_KEYS.SUBJECT_ID)
  const age = participantDataFields?.disabled?.some(data => data.metadata.internal_key === 'date_of_birth')
    ? undefined
    : yearDifference(date_of_birth, moment().format())
  renderFunctions.amount = ({ value }) => {
    const localizedPayment = convertToLocalizedNumber(value, language, true)
    return (
      <td key={`amount-${value}`} className='amount'>
        {localizedPayment}
      </td>
    )
  }

  const addParticipant = {
    content: 'Add This Participant',
    sendingContent: 'Adding',
    sentContent: '...Added!',
  }
  const saveChanges = {
    content: 'Save Changes',
    sendingContent: 'Saving',
    sentContent: '...Saved!',
  }
  const sendStateClassName = sendDisabled ? 'send-state' : '' // for cursor wait during sendState
  const site = ptpSiteLocation?.label
  const mainSite = ptpSiteLocation?.text

  const renderParticipantInfo = () => {
    return (
      <>
        <div className='flexed participant-info'>
          <div className='flexed info-row'>
            <TextOverflow text={participant.fname} tagClassName='no-margin title-overflow' width={200} />

            <span className='text text-blue'>
              {PARTICIPANT_ID}: {id}
            </span>
            {isPreScreenAllowed ? (
              <span className='text text-blue' style={{ marginLeft: !preScreenId ? '14px' : '0px' }}>
                {PRE_SCREEN_ID}: {preScreenId || EMPTY_VALUE}
              </span>
            ) : (
              <span className='text text-blue spacer'></span>
            )}
            <div className='ptp-site-location' style={{ background: ptpSiteLocation.color }}>
              <span>
                <b>{ptpSiteLocation.text}</b>
              </span>
            </div>
          </div>
          {!isCreate && !noPII && (
            <div className='flexed info-row'>
              {(age || age === 0) && (
                <span style={{ width: 200 }} className='no-margin title-overflow'>{`${age} y.o.`}</span>
              )}
              {isSubjectAllowed && (
                <span className='text text-blue'>
                  {SUBJECT_ID}: {participant?.subject_id || EMPTY_VALUE}
                </span>
              )}
              <span className='text text-blue spacer'></span>
              <span className='text text-blue'>
                {SITE_NAME}: {ptpSiteLocation?.label || EMPTY_VALUE}
              </span>
            </div>
          )}
        </div>
      </>
    )
  }

  const TAB_IDS = {
    instruments: 'instruments',
    consent: 'consent',
    diary: 'diary',
    announcements: 'announcements',
    visits: 'visits',
    files: 'files',
    payments: 'payments',
    trackManagement: 'track-management',
    armManagement: 'arm-management',
    accountSettings: 'account-settings',
    history: 'history',
  }

  const canViewAccountSettings = canResendInviteOrResetPw || canWrite || canResendInviteOrResetPw

  const visitTemplateSource = useMemo(() => {
    if (isVisitCycleSupported) return visitTemplates
    if (studyVisitSchedule?.length) return studyVisitSchedule
    return visitTemplates
  }, [isVisitCycleSupported, studyVisitSchedule, visitTemplates])

  const visitTemplatesList = useMemo(
    () =>
      visitTemplateSource.map(({ id: tid, name, visits }) => ({
        field: tid,
        label: name,
        value: (visits || []).map(({ id, name }) => ({
          key: id,
          text: name,
          value: id,
        })),
      })),
    [visitTemplateSource],
  )

  const hasVisitsInTemplates = useMemo(
    () =>
      visitTemplatesList.some(visitTemplate => {
        return !!visitTemplate.value.length
      }),
    [visitTemplatesList],
  )

  const getCyclesList = () => {
    if (!studyVisitSchedule || !isVisitCycleSupported) {
      return []
    }

    let phases = []

    if (isArmsSupported) {
      const selectedArm = studyVisitSchedule?.find(arm => arm.id === checkedArm?.key)
      if (selectedArm?.phases?.length) {
        phases = selectedArm?.phases
      }
    } else {
      phases = studyVisitSchedule?.reduce((acc, schedule) => {
        return [...acc, ...schedule?.phases]
      }, [])
    }

    const allCycles = phases?.reduce((acc, phase) => {
      return [...acc, ...phase?.cycles]
    }, [])

    return allCycles
  }

  const cycles = getCyclesList()

  const getFormattedCyclesList = () => {
    return cycles.map(cycle => {
      const formattedVisits = cycle.visits.map(visit => {
        return {
          key: visit.id,
          text: visit.name,
          value: visit.id,
        }
      })

      return {
        field: cycle.id,
        label: cycle.name,
        value: formattedVisits,
        isDummy: !!cycle.is_dummy,
      }
    })
  }

  const formattedCyclesList = getFormattedCyclesList()

  const hasVisitsInCycles = formattedCyclesList.some(visitTemplate => {
    return !!visitTemplate.value.length
  })

  const hasVisitsAvailable = isVisitCycleSupported ? hasVisitsInCycles : hasVisitsInTemplates
  const ptpHasVisitCycles = !!participantVisitCycles?.length
  const hasExistingParticipantVisits = !!participantVisits?.length
  const showVisitsUI = hasVisitsAvailable || hasExistingParticipantVisits || ptpHasVisitCycles

  // GOLD-12406 Option A: flag hides delivery/status only; tab + responses stay visible.
  const hideInstrumentStatus = shouldHideParticipantInstrumentStatus(study?.config, roleId)

  const studyHasVisitsConfigured = useMemo(() => {
    if (!hasVisitSupport) return false

    if (isVisitCycleSupported) {
      return (
        studyVisitSchedule?.some(arm =>
          arm?.phases?.some(phase => phase?.cycles?.some(cycle => cycle?.visits?.length > 0)),
        ) ?? false
      )
    }

    if (hasVisitsInTemplates) return true
    return studyVisitSchedule?.some(schedule => schedule?.visits?.length > 0) ?? false
  }, [hasVisitSupport, isVisitCycleSupported, studyVisitSchedule, hasVisitsInTemplates])

  const participantHasVisits = participantVisits?.length > 0
  const showVisitsTabInDrawer =
    !isCreate &&
    hasVisitSupport &&
    canViewVisitSchedule &&
    studyHasVisitsConfigured &&
    participantVisitsFetched &&
    participantHasVisits

  const TABS = [
    {
      id: TAB_IDS.instruments,
      title: STRINGS.instruments,
      visible: !isCreate,
    },
    {
      id: TAB_IDS.consent,
      title: STRINGS.consent,
      visible: !isCreate && consentRequired && !noPII,
    },
    {
      id: TAB_IDS.diary,
      title: STRINGS.diary,
      visible: !isCreate && isDiary && !hasInstrumentUnification,
    },
    {
      id: TAB_IDS.announcements,
      title: STRINGS.announcements,
      visible: !isCreate && hasAdvancedEventTimelines && canViewAnnouncements,
    },
    {
      id: TAB_IDS.visits,
      title: STRINGS.visits,
      visible: showVisitsTabInDrawer,
    },
    {
      id: TAB_IDS.trackManagement,
      title: STRINGS.track,
      visible: tracksEnabled,
    },
    {
      id: TAB_IDS.armManagement,
      title: STRINGS.arm,
      visible: isArmsSupported && isArmsDisplayed,
    },
    {
      id: TAB_IDS.files,
      title: STRINGS.files,
      visible: (!isCreate && canViewFiles) || !noPII,
    },
    {
      id: TAB_IDS.payments,
      title: STRINGS.payments,
      visible: !isCreate && hasIncentives,
    },
    {
      id: TAB_IDS.accountSettings,
      title: STRINGS.accountSettings,
      visible: inDrawer && canViewAccountSettings,
    },

    {
      id: TAB_IDS.history,
      title: STRINGS.history,
      visible: inDrawer && hasStudyPromotion && canViewHistory,
    },
  ]

  const visibleTabs = useMemo(() => {
    return TABS.filter(tab => tab.visible)
  }, [TABS])

  const [tab, setTab] = useState()

  useEffect(() => {
    if (ptpInstrumentsLoading === false) {
      if (!tab || !visibleTabs.some(visibleTab => visibleTab.id === tab.id)) {
        setTab(visibleTabs[0])
      }
    }
  }, [visibleTabs, tab, ptpInstrumentsLoading, instrumentStatus?.length])

  useEffect(() => {
    if (tab?.id === TAB_IDS.visits && !showVisitsTabInDrawer && visibleTabs.length > 0) {
      setTab(visibleTabs[0])
    }
  }, [showVisitsTabInDrawer, tab, visibleTabs])

  useEffect(() => {
    return () => {
      setTab(null)
    }
  }, [])

  const checkIsVisitNameFieldEmpty = () => {
    if (!hasVisitsInTemplates || !Object.keys(checkedVisitTemplate)?.length) {
      return false
    }

    if (Object.keys(checkedVisitTemplate)?.length) {
      return visitStartDatetime ? false : true
    }

    return false
  }

  const visitNameFieldEmpty = checkIsVisitNameFieldEmpty()

  const checkIsCycleVisitNameFieldEmpty = () => {
    if (!Object.keys(checkedVisitCycle)?.length) {
      return false
    }

    if (isCreate) {
      if (hasVisitsInCycles) {
        return visitStartDatetime ? false : true
      }
      return false
    } else {
      return !hasVisitsInCycles && !visitStartDatetime
    }
  }

  const cycleVisitNameFieldEmpty = checkIsCycleVisitNameFieldEmpty()

  const checkVisitsErrors = () => {
    if (isEnroller) {
      return false
    }

    return isVisitCycleSupported ? cycleVisitNameFieldEmpty : visitNameFieldEmpty
  }

  const checkArmsErrors = () => {
    if (isEnroller) {
      return false
    }

    return isArmsSupported ? !checkedArm : false
  }

  const isValidPtp = onValidateParticipant()
  // Only block submit on subject ID availability if a subject ID is actually provided.
  const hasSubjectIdValue = !!(subject_id && String(subject_id).trim())
  const hasSubjectIdConflict = hasSubjectIdValue && isSubjectIdAvailable === false
  const isSubmitBtnDisabled =
    sendDisabled || // global sending/disabled state
    studyLock || // study locked
    hasSubjectIdConflict || // subject ID provided but not available
    !isValidPtp || // form validation failed
    checkVisitsErrors() || // visit-level visit errors
    checkArmsErrors() // arm-selection errors

  const getArmsList = () => {
    const list = studyVisitSchedule?.length ? studyVisitSchedule : []

    return list.map(item => ({
      key: item?.id,
      value: item?.id,
      text: item?.name,
    }))
  }

  const armsList = getArmsList()

  useEffect(() => {
    if (!checkedArm) {
      if (participant?.study_schedule_id) {
        // Preselect Arm if participant already has assigned Arm
        const participantArm = armsList.find(arm => arm?.key === participant?.study_schedule_id)
        setCheckedArm(participantArm)
      } else if (armsList?.length === 1) {
        // Preselect Arm if there is only 1 Arm in the study
        setCheckedArm(armsList[0])
      }
    }
  }, [armsList, checkedArm, participant])

  const onArmToggle = item => {
    if (checkedArm?.key === item) {
      setCheckedArm(undefined)
      setCheckedVisitCycle({})
      updateField('start_visit_id', '')
      updateField('visit_start_datetime', '')
      setVisitTemplateName('')
    } else {
      const selectedArm = armsList.find(arm => arm?.key === item)
      setCheckedArm(selectedArm)
      setCheckedVisitCycle({})
      updateField('start_visit_id', '')
      updateField('visit_start_datetime', '')
      setVisitTemplateName('')
    }
  }

  return (
    <div className={`participant page ${sendStateClassName} ${inDrawer ? 'drawer-container' : ''}`} ref={pageRef}>
      {isCreate && <CreateParticipantNav {...props} onSaveParticipant={onSaveParticipant} />}
      {isCreate || id ? (
        <>
          <div className={inDrawer ? 'ptp-drawer-buttons' : ''}>
            {inDrawer && !isClinOrSiteCoordinator && (
              <div className='flexed-header compliance'>
                <span className='compliance-rate'>
                  <b>{getCompletion()}</b>
                </span>
              </div>
            )}
            {link && canWrite && (
              <div className='flexed-header end-justified'>
                <Button
                  disabled={studyLock}
                  grey
                  id='participant-edit-profile'
                  content='Edit Profile'
                  onClick={() => {
                    closeDrawer()
                    browserHistory.push(link)
                  }}
                />
              </div>
            )}
          </div>
          <div className={inDrawer ? 'ptp-sticky-info' : ''}>
            {isCreate ? (
              <div className='flexed-header'>
                <h2 className='ptp-name'>New Participant</h2>
              </div>
            ) : (
              <Container className={`container-sticky ${inDrawer ? 'drawer-container-sticky' : ''}`}>
                {renderParticipantInfo()}
              </Container>
            )}
          </div>
          <div className={`ptp-container-wrapper ${inDrawer ? 'drawer-container-wrapper' : ''}`}>
            {inDrawer ? (
              <>
                <div className='drawer-info-section'>
                  <StudyTimeline study={study} participant={participant} />
                  {!isCreate && <ParticipantStats isClinOrSiteCoordinator={isClinOrSiteCoordinator} {...props} />}
                </div>
                <DrawerTabs
                  tabs={visibleTabs}
                  selectedTab={tab}
                  selectTab={newTab => {
                    setTab(newTab)
                  }}
                />
                <div className='drawer-tab-content'>
                  {tab?.id == TAB_IDS.instruments && (
                    <CompletedInstruments
                      {...props}
                      ptpName={name}
                      hasAdvancedEventTimelines={hasAdvancedEventTimelines}
                      hideInstrumentStatus={hideInstrumentStatus}
                      isV2Architecture={isV2Architecture}
                      ptpInstrumentsLoading={ptpInstrumentsLoading}
                      instrumentStatus={instrumentStatus}
                      site={site}
                      mainSite={mainSite}
                    />
                  )}
                  {tab?.id == TAB_IDS.consent && (
                    <div>
                      <NewConsentManagement disabled={!canUpdateConsent || studyLock} {...props} ptpName={name} />
                    </div>
                  )}
                  {tab?.id == TAB_IDS.diary && (
                    <div>
                      <CompletedDiaries {...props} hasAdvancedEventTimelines={hasAdvancedEventTimelines} />
                    </div>
                  )}
                  {tab?.id == TAB_IDS.announcements && hideDinamically.announcements && (
                    <CompletedAnnouncements {...props} ptpName={name} site={site} mainSite={mainSite} />
                  )}
                  {tab?.id == TAB_IDS.visits && hideDinamically.visits && (
                    <div>
                      <ParticipantVisits {...props} />
                      {isVisitCycleSupported && ptpHasVisitCycles ? <ParticipantVisitCycle {...props} /> : null}
                    </div>
                  )}
                  {tab?.id == TAB_IDS.trackManagement && tracksEnabled && (
                    <div>
                      <CohortManagement
                        onChange={triggerEditable}
                        disabled={inDrawer || !(canWrite || (isCreate && canAddPtp)) || sendDisabled}
                        ptpName={name}
                        title={STRINGS.trackManagement}
                        isReadOnlyState={isReadOnlyState}
                      />
                    </div>
                  )}
                  {tab?.id == TAB_IDS.armManagement && isArmsSupported && isArmsDisplayed ? (
                    <div>
                      <ParticipantArms ptpName={name} ptpArm={checkedArm} />
                    </div>
                  ) : null}
                  {tab?.id == TAB_IDS.files && (
                    <div>
                      <FilesManagement {...props} ptpName={name} />
                    </div>
                  )}
                  {tab?.id == TAB_IDS.payments && hideDinamically.payments ? renderPaymentsSection(name) : null}
                  {tab?.id == TAB_IDS.accountSettings && hideDinamically.accountSettings && (
                    <ParticipantProfile
                      {...props}
                      accountSettings={renderAccountDeletion(
                        deactivation_request_timestamp,
                        canWrite || canResendInviteOrResetPw,
                      )}
                    />
                  )}
                  {tab?.id == TAB_IDS.history ? <ParticipantHistory {...props} /> : null}
                </div>
              </>
            ) : (
              <>
                {!isCreate && <ParticipantStats isClinOrSiteCoordinator={isClinOrSiteCoordinator} {...props} />}
                <ParticipantForm
                  isCaregiverV1={isCaregiverV1}
                  disabled={!(canWrite || canAddPtp) || sendDisabled}
                  needToReassign={needToReassign}
                  editable={editable}
                  triggerEditable={triggerEditable}
                  {...props}
                  subjectIdSiteBased={subjectIdSiteBased}
                  locales={
                    study.config && study.config.enforced_language ? study.config.enforced_language.languages : locales
                  }
                  participantDataFields={participantDataFields}
                  studyId={study?.id}
                  isSubjectIdAvailable={isSubjectIdAvailable}
                  participantId={ptpId}
                />
                {!isCreate && canMove && (
                  <div>
                    <ParticipantStudySettings {...props} />
                  </div>
                )}
                {!isCreate && consentRequired && !noPII && (
                  <div>
                    <NewConsentManagement disabled={!canUpdateConsent || studyLock} {...props} ptpName={name} />
                  </div>
                )}
                {!isCreate && hasVisitSupport && canViewVisitSchedule && showVisitsUI && hideDinamically.visits && (
                  <div>
                    <ParticipantVisits {...props} />
                  </div>
                )}
                {!isCreate && !noPII && canViewFiles && (
                  <div>
                    <FilesManagement {...props} ptpName={name} />
                  </div>
                )}
                {tracksEnabled && (
                  <div>
                    <CohortManagement
                      onChange={triggerEditable}
                      disabled={inDrawer || !(canWrite || (isCreate && canAddPtp)) || sendDisabled}
                      ptpName={name}
                      title='Track Management'
                      isReadOnlyState={isReadOnlyState}
                    />
                  </div>
                )}
                {isArmsSupported && isArmsDisplayed ? (
                  <ArmManagement
                    loading={false}
                    disabled={false}
                    hasError={!!armError}
                    errorText={armError}
                    checked={checkedArm}
                    list={armsList}
                    onToggleItem={item => {
                      // TODO: Potentially add a checking participant?.study_schedule_id !== item.key
                      if (!isCreate) {
                        openModal({
                          heading: MODAL_CONTENT_MAP.pleaseConfirm,
                          content: MODAL_CONTENT_MAP.participantNewArm,
                          confirmButton: MODAL_BUTTONS_MAP.confirm,
                          cancelButton: MODAL_BUTTONS_MAP.cancel,
                          className: MODAL_CLASSES_MAP.confirmation,
                          onConfirm: () => {
                            onArmToggle(item)
                            closeModal()
                          },
                        })
                      } else {
                        onArmToggle(item)
                      }
                    }}
                  />
                ) : null}
                {!isCreate && hasIncentives && hideDinamically.payments && renderPaymentsSection(name)}
                {!isCreate && hasAdvancedEventTimelines && hideDinamically.announcements && (
                  <CompletedAnnouncements {...props} ptpName={name} />
                )}
                {!isCreate && (
                  <CompletedInstruments
                    {...props}
                    ptpName={name}
                    hasAdvancedEventTimelines={hasAdvancedEventTimelines}
                    hideInstrumentStatus={hideInstrumentStatus}
                    isV2Architecture={isV2Architecture}
                    ptpInstrumentsLoading={ptpInstrumentsLoading}
                    instrumentStatus={instrumentStatus}
                    site={site}
                    mainSite={mainSite}
                  />
                )}
                {!isCreate && isDiary && (
                  <div>
                    <CompletedDiaries {...props} hasAdvancedEventTimelines={hasAdvancedEventTimelines} />
                  </div>
                )}
                {canViewVisitSchedule && hasVisitSupport && hasVisitsAvailable && (
                  <VisitScheduleManagement
                    studyID={studyID}
                    visitTemplates={visitTemplates}
                    fetchVisitTemplates={fetchVisitTemplates}
                    updateField={updateField}
                    participant={participant}
                    disabled={sendDisabled || (isArmsSupported ? !checkedArm : false)}
                    isCreate={isCreate}
                    clearPtpVisits={clearPtpVisits}
                    clearPtpVisitCycles={clearPtpVisitCycles}
                    fetchParticipantVisits={fetchParticipantVisits}
                    participantVisits={participantVisits}
                    visitTemplatesList={visitTemplatesList}
                    visitCyclesList={formattedCyclesList}
                    hasVisitsInTemplates={hasVisitsInTemplates}
                    hasVisitsInCycles={hasVisitsInCycles}
                    checkedVisitTemplate={checkedVisitTemplate}
                    checkedVisitCycle={checkedVisitCycle}
                    setCheckedVisitTemplate={setCheckedVisitTemplate}
                    setCheckedVisitCycle={setCheckedVisitCycle}
                    isVisitCycleSupported={isVisitCycleSupported}
                    visitTemplateName={visitTemplateName}
                    setVisitTemplateName={setVisitTemplateName}
                  />
                )}
                {editable && canSave && (
                  <div className='button-list'>
                    <Button
                      onClick={() => onOpenCancelModal(props)}
                      className='grey'
                      id='create-participant-cancel'
                      content='Cancel'
                      disabled={sendDisabled}
                    />
                    <Button
                      onClick={onSaveParticipant}
                      id='participant-save'
                      content={buttonContentOnSendState(
                        isCreate ? addParticipant : saveChanges,
                        requestSending,
                        requestSent,
                      )}
                      disabled={isSubmitBtnDisabled}
                      loading={requestSending}
                    />
                  </div>
                )}
                {!isCreate && hideDinamically.accountSettings && <AccountSettings {...props} />}
              </>
            )}
          </div>
        </>
      ) : (
        <Loader />
      )}
    </div>
  )
}

CreateParticipantPage.propTypes = {
  baseSiteID: PropTypes.number,
  canAddPtp: PropTypes.bool,
  canMove: PropTypes.bool,
  canResendInviteOrResetPw: PropTypes.bool,
  canSeeOrphans: PropTypes.bool,
  canUpdateConsent: PropTypes.bool,
  canViewAnnouncements: PropTypes.bool,
  canViewFiles: PropTypes.bool,
  canViewVisitSchedule: PropTypes.bool,
  canWrite: PropTypes.bool,
  checkedCohorts: PropTypes.object,
  checkedSites: PropTypes.object,
  clearPtpVisits: PropTypes.func,
  closeDrawer: PropTypes.func,
  closeModal: PropTypes.func,
  componentLoading: PropTypes.shape({ pendingPayments: PropTypes.bool }),
  consentRequired: PropTypes.bool,
  fetchConsentEvents: PropTypes.func,
  fetchLocales: PropTypes.func,
  fetchParticipantVisits: PropTypes.func,
  fetchPtp: PropTypes.func,
  fetchPtpPayments: PropTypes.func,
  fetchVisitTemplates: PropTypes.func,
  hasIncentives: PropTypes.bool,
  hasOrphanedParticipants: PropTypes.bool,
  hasTreasureChest: PropTypes.bool,
  hasVisitSupport: PropTypes.bool,
  inDrawer: PropTypes.bool,
  initializeEditPage: PropTypes.func,
  inLeafSite: PropTypes.bool,
  isCreate: PropTypes.bool,
  isDiary: PropTypes.bool,
  isEmailless: PropTypes.bool,
  isInParticipantsTable: PropTypes.bool,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  link: PropTypes.string,
  locales: PropTypes.arrayOf(PropTypes.object),
  noPII: PropTypes.bool,
  tracksEnabled: PropTypes.bool,
  onSetOrphanedParticipants: PropTypes.func,
  openModal: PropTypes.func,
  params: PropTypes.object,
  params: PropTypes.object,
  participant: PropTypes.object,
  participantDataFields: PropTypes.object,
  participantVisits: PropTypes.arrayOf(PropTypes.array),
  payments: PropTypes.object,
  ptpId: PropTypes.number,
  ptpSiteID: PropTypes.number,
  requestSending: PropTypes.bool,
  requestSent: PropTypes.bool,
  resetCheckedSites: PropTypes.func,
  roleId: PropTypes.number,
  saveToDatabase: PropTypes.func,
  sendDisabled: PropTypes.bool,
  setNavBarProps: PropTypes.func,
  sitesInfo: PropTypes.shape(),
  study: PropTypes.object,
  studyID: PropTypes.number,
  studyLock: PropTypes.bool,
  subjectIdSiteBased: PropTypes.bool,
  toggleOnSite: PropTypes.func,
  toggleSite: PropTypes.func,
  updateField: PropTypes.func,
  userId: PropTypes.number,
  visitTemplates: PropTypes.arrayOf(PropTypes.object),
}

export default CreateParticipantPage
